import { ReactNode } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';

import {
  Alert,
  Autocomplete,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CustomButton } from '../customButton/CustomButton';
import { SelectableWrapper } from '../selectableWrapper/SelectableWrapper';
import { TextButton } from '../textButton/TextButton';

import {
  API_STATUS,
  APP_ROUTES,
  PositionSubscriptionCountries,
  PositionSubscriptionFrequencyOptions,
  PositionSubscriptionTechnicalFlows,
  PositionSubscriptionType,
} from 'src/constants';
import {
  PositionSubscriptionFields,
  PositionSubscriptionFormik,
} from 'src/types';
import { formatSkillLabel, isStatusComplete, isStatusLoading } from 'src/utils';

const getErrorByFieldName = (
  field: keyof PositionSubscriptionFields,
  formik: PositionSubscriptionFormik,
): string | undefined =>
  formik.errors[field] && (formik.touched[field] || formik.submitCount)
    ? formik.errors[field]
    : undefined;

export const PositionSubscriptionForm: React.FC<{
  title?: string | ReactNode;
  initialValues?: PositionSubscriptionFields;
  requiredFields: Array<keyof PositionSubscriptionFields>;
  disabledFields?: Array<keyof PositionSubscriptionFields>;
  validationSchema: yup.ObjectSchema<any>;
  type: PositionSubscriptionType;
  submissionBtnLabel: string;
  apiStatus: API_STATUS;
  successMessage?: string;
  onSubmit: (values: PositionSubscriptionFields) => void;
}> = ({
  title,
  initialValues,
  requiredFields,
  disabledFields,
  validationSchema,
  type,
  submissionBtnLabel,
  apiStatus,
  successMessage,
  onSubmit,
}) => {
  const navigate = useNavigate();

  const formik = useFormik<PositionSubscriptionFields>({
    initialValues: initialValues || {
      email: null,
      name: null,
      locations: null,
      technicalFlows: null,
      experience: null,
      frequency: null,
    },
    validationSchema,
    onSubmit: (values) => {
      onSubmit(values);
      formik.resetForm();
    },
    enableReinitialize: true,
  });

  const isLoading = isStatusLoading(apiStatus);

  const emailError = getErrorByFieldName('email', formik);
  const nameError = getErrorByFieldName('name', formik);
  const locationsError = getErrorByFieldName('locations', formik);
  const technicalFlowsError = getErrorByFieldName('technicalFlows', formik);
  const experienceError = getErrorByFieldName('experience', formik);
  const frequencyError = getErrorByFieldName('frequency', formik);

  const handleRefereeFormClick = async () => {
    navigate(`/${APP_ROUTES.REFER_FRIEND_POSITION_SUBSCRIPTION}`);
  };

  return (
    <Stack gap={4} width="100%">
      {!!title && (
        <Typography textAlign="left" variant="h1" mb={0}>
          {title}
        </Typography>
      )}
      <Stack gap={2}>
        <Stack gap={1}>
          <TextField
            name="email"
            type="email"
            placeholder="Email"
            label="Email"
            id="email"
            variant="outlined"
            disabled={disabledFields?.includes('email') || isLoading}
            required={requiredFields.includes('email')}
            value={formik.values.email || ''}
            onChange={(e) =>
              formik.setFieldValue('email', e.target.value || null)
            }
            onBlur={formik.handleBlur}
            error={!!emailError}
            helperText={emailError}
          />
          {type === PositionSubscriptionType.Candidate && (
            <TextButton
              onClick={handleRefereeFormClick}
              variant="primary"
              sx={{ justifyContent: 'left' }}
            >
              AgileEngineer? Use this form →
            </TextButton>
          )}
        </Stack>

        {type === PositionSubscriptionType.Candidate && (
          <TextField
            name="name"
            type="text"
            placeholder="Name"
            label="Name"
            id="name"
            variant="outlined"
            disabled={disabledFields?.includes('name') || isLoading}
            required={requiredFields.includes('name')}
            value={formik.values.name || ''}
            onChange={(e) =>
              formik.setFieldValue('name', e.target.value || null)
            }
            onBlur={formik.handleBlur}
            error={!!nameError}
            helperText={nameError}
          />
        )}
        <Autocomplete
          value={formik.values.locations || []}
          options={PositionSubscriptionCountries}
          onChange={(_, value) =>
            formik.setFieldValue('locations', value || null)
          }
          onBlur={() => formik.setFieldTouched('locations')}
          multiple
          filterSelectedOptions
          disabled={disabledFields?.includes('locations') || isLoading}
          sx={{
            '& .MuiInputLabel-root': {
              transform: 'translate(14px, 16px) scale(1) !important',
            },
            '& .MuiInputLabel-root.Mui-focused, & .MuiInputLabel-root.MuiFormLabel-filled, & .MuiInputLabel-shrink':
              {
                transform: 'translate(14px, -9px) scale(0.75) !important',
              },
            '& .MuiOutlinedInput-notchedOutline': {
              '& legend': {
                display: 'block !important',
              },
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              name="locations"
              label="Country of residence"
              required={requiredFields.includes('locations')}
              error={!!locationsError}
              helperText={locationsError}
            />
          )}
        />

        <Autocomplete
          value={formik.values.technicalFlows || []}
          options={PositionSubscriptionTechnicalFlows}
          getOptionLabel={formatSkillLabel}
          onChange={(_, value) =>
            formik.setFieldValue('technicalFlows', value.length ? value : null)
          }
          onBlur={() => formik.setFieldTouched('technicalFlows')}
          multiple
          filterSelectedOptions
          disabled={disabledFields?.includes('technicalFlows') || isLoading}
          sx={{
            '& .MuiInputLabel-root': {
              transform: 'translate(14px, 16px) scale(1) !important',
            },
            '& .MuiInputLabel-root.Mui-focused, & .MuiInputLabel-root.MuiFormLabel-filled, & .MuiInputLabel-shrink':
              {
                transform: 'translate(14px, -9px) scale(0.75) !important',
              },
            '& .MuiOutlinedInput-notchedOutline': {
              '& legend': {
                display: 'block !important',
              },
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Specialization"
              required={requiredFields.includes('technicalFlows')}
              error={!!technicalFlowsError}
              helperText={technicalFlowsError}
            />
          )}
        />
        {type === PositionSubscriptionType.Candidate && (
          <TextField
            name="experience"
            type="number"
            placeholder="Years of experience"
            label="Years of experience"
            id="experience"
            variant="outlined"
            disabled={disabledFields?.includes('experience') || isLoading}
            required={requiredFields.includes('experience')}
            value={formik.values.experience || ''}
            onChange={(e) =>
              formik.setFieldValue(
                'experience',
                e.target.value ? Number(e.target.value) : null,
              )
            }
            onBlur={formik.handleBlur}
            error={!!experienceError}
            helperText={experienceError}
          />
        )}
        {type === PositionSubscriptionType.Referral && (
          <>
            <Stack spacing={1} direction="row" width="100%">
              {PositionSubscriptionFrequencyOptions.map((option) => {
                return (
                  <SelectableWrapper
                    value={option.value}
                    selectedValue={formik.values.frequency}
                    onClick={() =>
                      formik.setFieldValue('frequency', option.value)
                    }
                    isDisabled={isLoading}
                    sx={{ width: '100%' }}
                  >
                    <Typography noWrap>{option.label}</Typography>
                  </SelectableWrapper>
                );
              })}
            </Stack>
            {!!frequencyError && (
              <Typography
                variant="body2"
                textAlign="left"
                ml={2}
                sx={{ color: 'rgba(255, 46, 0, 1)' }}
              >
                {frequencyError}
              </Typography>
            )}
          </>
        )}
      </Stack>
      {!!successMessage && !formik.dirty && isStatusComplete(apiStatus) && (
        <Alert
          icon={false}
          severity="warning"
          sx={{ backgroundColor: '#fff4cc' }}
        >
          <Typography color="text.primary">{successMessage}</Typography>
        </Alert>
      )}
      <CustomButton
        color="primary"
        label={submissionBtnLabel}
        onClick={() => formik.handleSubmit()}
        isLoading={isLoading}
        isDisabled={!formik.dirty || isLoading}
      />
    </Stack>
  );
};
